import { useState } from "react";
import { Check, Copy, Landmark } from "lucide-react";

const CopyBtn = ({ value, label, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button type="button" onClick={copy} data-testid={testId}
      className="inline-flex items-center gap-1 rounded-lg border border-hz-gray/50 px-2 py-1 text-xs font-bold text-hz-navy transition-colors hover:border-hz-cyan hover:bg-hz-cyan/5">
      {copied ? <><Check className="h-3.5 w-3.5 text-hz-success" /> Copiado</> : <><Copy className="h-3.5 w-3.5" /> {label}</>}
    </button>
  );
};

const Row = ({ label, value, copyLabel, testId }) => (
  <div className="flex items-center justify-between gap-2 py-1" data-testid={testId}>
    <span className="text-hz-ink/60">{label}</span>
    <span className="flex items-center gap-2 font-semibold text-hz-ink">
      <span className="break-all text-right tracking-wide">{value}</span>
      {copyLabel && <CopyBtn value={value} label={copyLabel} testId={`${testId}-copy`} />}
    </span>
  </div>
);

export const TransferAccountsCard = ({ accounts, folio, testId = "transfer-accounts" }) => {
  if (accounts == null) return null;
  if (accounts.length === 0) {
    return (
      <div className="rounded-xl border border-hz-warning/40 bg-hz-warning/5 p-4" data-testid={`${testId}-empty`}>
        <p className="text-sm font-bold text-hz-navy">Datos para realizar tu transferencia</p>
        <p className="mt-1 text-xs text-hz-ink/70">Comunícate con el equipo organizador para recibir instrucciones de pago.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-hz-gray/40 bg-white p-4" data-testid={testId}>
      <p className="flex items-center gap-2 text-sm font-bold text-hz-navy">
        <Landmark className="h-4 w-4 text-hz-cyan" /> Datos para realizar tu transferencia
      </p>
      <p className="mt-1 text-xs leading-relaxed text-hz-ink/70">
        Puedes realizar tu pago a cualquiera de las siguientes cuentas. Utiliza tu folio como referencia de pago.
      </p>
      <p className="mt-2 rounded-lg bg-hz-navy/5 px-3 py-1.5 text-center text-xs font-extrabold tracking-widest text-hz-navy" data-testid={`${testId}-reference`}>
        Referencia: {folio}
      </p>
      <div className="mt-3 space-y-3">
        {accounts.map((a, i) => (
          <div key={a.id || i} className="rounded-lg border border-hz-gray/30 px-3 py-2 text-sm" data-testid={`${testId}-account-${i}`}>
            <div className="flex items-center justify-between">
              <p className="font-extrabold text-hz-navy">{a.label}</p>
              {a.is_primary && <span className="rounded-full bg-hz-cyan/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-hz-cyan">Principal</span>}
            </div>
            <div className="mt-1 divide-y divide-hz-gray/20">
              {a.bank_name && <Row label="Banco" value={a.bank_name} testId={`${testId}-bank-${i}`} />}
              {a.account_holder && <Row label="Titular" value={a.account_holder} testId={`${testId}-holder-${i}`} />}
              {a.clabe && <Row label="CLABE" value={a.clabe} copyLabel="Copiar CLABE" testId={`${testId}-clabe-${i}`} />}
              {a.account_number && <Row label="Cuenta" value={a.account_number} copyLabel="Copiar cuenta" testId={`${testId}-account-number-${i}`} />}
              {a.card_number && <Row label="Tarjeta" value={a.card_number} copyLabel="Copiar tarjeta" testId={`${testId}-card-${i}`} />}
            </div>
            {a.instructions && <p className="mt-1.5 text-xs leading-relaxed text-hz-ink/70">{a.instructions}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};
